import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataField,
  Field,
  FieldValue,
  InfoNote,
} from '@ghp/ui'
import {
  BadgeCheck,
  CalendarClock,
  CheckCheck,
  ClipboardCheck,
  Info,
  Lock,
  PenTool,
  Send,
  TriangleAlert,
  Undo2,
} from 'lucide-react'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import {
  CERTIFICATION_MENTIONS,
  MAX_SCHEMATIC_NODES,
  appointmentDeviation,
  appointmentSlot,
  dayOf,
  describeAppointmentDeviation,
  fixtureDesignationWarning,
  fixtureFlagWarning,
  fixturesToFlag,
  stormwaterWarnings,
  hasTruncatedSchematicStroke,
  isSchematicPlanFull,
  recordedAnomalyCount,
  resolveInspectionResult,
  type AppointmentContact,
  type AppointmentDeviation,
  type VocabularyEntry,
} from '@ghp/domain'

import type { InspectionDraft, LocalMedium, OutboxState } from '../../../offline/data-db'
import { isOtherPersonPresent, metPersonOf } from '../../../offline/met-person'
import { RESULT_PRESENTATION, stampLabel, SYNC_PRESENTATION } from '../labels'
import { hasOutdatedSchematicImage } from '@ghp/schematic'
import { isAttachment } from './AttachmentsTab'
import { AttendeeCard, AttendeeFields } from './AttendeeCard'
import { ContactsCard } from './ContactsCard'
import { SignaturePad } from './SignaturePad'
import s from './SubmitTab.module.css'
import claim from './claimed.module.css'

/**
 * Onglet « Transmettre » — personne rencontrée, mentions, signature, envoi.
 *
 * Deux règles s'y croisent, et elles ne disent pas la même chose :
 *
 * - **le seul verrou dur** est la signature, qui exige la mention des points d'eau (M20) : un
 *   tiers ne peut pas attester de ce qu'on ne lui a pas énoncé ;
 * - **tout le reste avertit** (M21). Qualité du signataire absente, relevé vide, aucune
 *   pièce : l'écran le dit au-dessus du bouton, et le bouton reste actif. Bloquer, ce serait
 *   laisser l'agent devant la maison avec une fiche qu'il ne peut pas rendre.
 */
export interface SubmitTabProps {
  draft: InspectionDraft
  media: LocalMedium[]
  /**
   * Le rendez-vous pris pour ce contrôle, `null` quand il n'y en a pas — descendu avec la
   * tournée. Il ne sert **qu'à comparer** : la carte « Date du contrôle » le montre à côté du
   * jour du passage et signale l'écart. Rien ne l'écrit, rien ne s'aligne dessus (#115, M21).
   *
   * ⚠️ **Requise, et sans défaut** — comme les autres données de l'écran. Un `= null` implicite
   * laissait compiler un appelant qui l'oublie, et la carte affirmait alors « Aucun rendez-vous
   * enregistré » pour une demande qui en a un.
   */
  scheduledAt: string | null
  /**
   * Le contact de la demande : c'est lui qui signe quand personne d'autre n'est sur place (#555).
   * Absent, la fiche ne compte que sur la carte du signataire.
   */
  contact?: AppointmentContact | null
  /** Fiche déjà transmise : on ne la rejoue pas, on montre où en est l'envoi. */
  locked: boolean
  /**
   * Le bureau a rendu ce contrôle à l'équipe (état de tournée `returned`, #181) : transmis, oui,
   * mais **pas** clos. Sans cette nuance, la carte d'envoi annonçait « il ne peut plus être
   * modifié » sur la fiche même que le bureau demande de corriger.
   */
  returned?: boolean
  sync: OutboxState | null
  /** Le motif consigné par la file quand l'envoi a échoué ou a été refusé (M21). */
  syncError?: string | null
  /**
   * Tentatives d'envoi déjà faites. C'est le retour visible de « Réessayer l'envoi » : sans
   * lui, un rejeu qui échoue à l'identique est indistinguable d'un bouton mort.
   */
  syncAttempts?: number
  /**
   * Le chemin vers l'**Historique**, rendu sous l'accusé de transmission — le prototype y
   * conduit de lui-même (`transmit`, `go('historique')`), et sans lui l'agent reste sur une
   * fiche verrouillée dont la ligne vient de quitter « À faire ».
   *
   * ⚠️ **Il n'est montré que sur un envoi accepté**, parce que c'est le seul cas où
   * l'historique porte ce contrôle : il liste les envois `sent` (`routes/history.tsx`), et une
   * transmission encore en file laisserait l'agent devant une liste où il ne se trouve pas —
   * or c'est aussi le cas où sa ligne est **restée** dans « À faire » (`tourStateOf`), donc où
   * « Retour » le ramène bien où il croit aller.
   */
  history?: ReactNode
  /** Le référentiel en cache : les mots de l'avertissement d'une anomalie désignée (#312). */
  vocabulary?: readonly VocabularyEntry[]
  onDraftChange?: (patch: Partial<InspectionDraft>) => void
  onSign?: (signature: Blob) => void
  /** « Effacer » sous le canvas : retire aussi la signature déjà rangée. */
  onSignatureClear?: () => void
  onSubmit?: () => void
  /** Relancer l'envoi sans attendre le minuteur — l'agent sait quand le réseau est revenu. */
  onRetrySend?: () => void
  /**
   * Rouvrir une fiche **refusée** (`rejected`) : le 409 de dépôt veut dire « rien n'est
   * enregistré », et sans cette sortie la fiche serait verrouillée sur la tablette *et*
   * refusée par le serveur — le relevé n'existerait plus nulle part d'accessible.
   */
  onReopen?: () => void
  /**
   * Reprendre un contrôle **renvoyé** (#228) : même geste que ci-dessus pour l'agent, autre
   * mécanique — le dépôt d'origine, lui, a été accepté, et la correction repart donc sous une
   * clé d'envoi neuve. Le bandeau de tête de fiche porte le même bouton.
   */
  onReopenReturned?: () => void
}

/**
 * Les deux mentions du CDC, **verbatim du prototype validé**, puis le consentement de la frame
 * Terrain 1.10b (#354, M37) : ce sont les phrases lues au signataire. Elles vivent dans le domaine (`CERTIFICATION_MENTIONS`), parce que le bureau et le
 * rapport PDF les impriment aussi — et qu'une copie a déjà dérivé.
 *
 * `required` est celle que le prototype marque d'un astérisque : elle seule débloque la
 * signature, et c'est l'unique différence visible entre deux phrases de trois lignes.
 */
const CERTIFICATIONS = [
  {
    key: 'certifiedWaterPoints',
    label: CERTIFICATION_MENTIONS.certifiedWaterPoints,
    required: true,
  },
  {
    key: 'certifiedNoSepticTank',
    label: CERTIFICATION_MENTIONS.certifiedNoSepticTank,
    required: false,
  },
  {
    key: 'consentedDataRetention',
    label: CERTIFICATION_MENTIONS.consentedDataRetention,
    required: false,
  },
] as const

export function SubmitTab({
  draft,
  media,
  scheduledAt,
  contact = null,
  locked,
  returned = false,
  sync,
  syncError,
  syncAttempts,
  history,
  vocabulary = [],
  onDraftChange,
  onSign,
  onSignatureClear,
  onSubmit,
  onRetrySend,
  onReopen,
  onReopenReturned,
}: SubmitTabProps) {
  const warningsId = useId()

  // Le contrat de l'écran : sans rappel d'écriture, tout se lit et rien ne se saisit —
  // sinon la frappe s'affiche dans le DOM et n'est écrite nulle part.
  const readOnly = locked || onDraftChange === undefined

  /**
   * Transmettre est le seul acte de l'écran que M21 autorise à interposer : il est
   * **irréversible** côté tablette, et rien ne le disait avant le geste. Deux temps donc —
   * un premier tap qui énonce ce que le geste engage, un second qui confirme. `submitted`
   * gèle le bouton entre la confirmation et le verrou relu depuis Dexie : un double-tap ne
   * lance qu'une seule chaîne d'envoi.
   */
  const [confirming, setConfirming] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  /*
   * Le jour du passage se pose au jour de la tablette quand la fiche arrive ici sans date :
   * l'agent y fait signer sur place, et changer ce jour est l'exception (24/09). Pas à
   * l'ouverture de la fiche — une tournée se prépare la veille (#115). Une seule fois par
   * affichage, pour qu'un jour que l'agent efface reste effacé.
   */
  const dayPrefilled = useRef(false)
  useEffect(() => {
    if (dayPrefilled.current || readOnly || !isBlank(draft.performedOn)) return
    dayPrefilled.current = true
    onDraftChange?.({ performedOn: dayOf(new Date()) })
  }, [readOnly, draft.performedOn, onDraftChange])

  /*
   * ⚠️ **La seule mention obligatoire est celle des points d'eau** — le prototype ne marque
   * qu'elle (`*`, « obligatoire pour signer »). Exiger aussi l'absence de fosse verrouillait
   * la signature devant toute propriété **qui en a une** : l'agent ne pouvait pas cocher
   * honnêtement, donc ne pouvait ni faire signer ni transmettre. La seconde mention s'écrit
   * dans la proposition et ne conditionne rien.
   */
  const certified = draft.certifiedWaterPoints
  const deviation = appointmentDeviation(scheduledAt, draft.performedOn)
  // Le signataire tel qu'il partira : le contact quand personne d'autre n'est sur place (#555).
  const signed = { ...draft, metPerson: metPersonOf(draft, contact) }
  const warnings = submissionWarnings(signed, media, deviation, vocabulary)
  const syncing = sync === null ? null : SYNC_PRESENTATION[sync]
  // La conclusion **surcharge comprise** (M19) : la même lecture que l'onglet « Contrôle »,
  // pour que le dernier écran avant la transmission n'annonce pas autre chose que lui.
  const conclusion = RESULT_PRESENTATION[resolveInspectionResult(draft).value]
  const recorded = recordedAnomalyCount(draft)
  const sealed = isSignatureSealed(draft)
  const signedOn = draft.signedAt === null ? null : appointmentSlot(draft.signedAt)
  const ink = media.find((medium) => medium.id === draft.signatureMediaId)?.blob

  return (
    <div className={s.sections}>
      <div className={s.column}>
        {/* Les mêmes cartes qu'en « Infos », sur le même brouillon (frames 1.10c / 1.10d, recette
          du 20/10, #554) : qui signe se lit et se corrige au-dessus de la signature — le contact
          tant que personne d'autre n'est sur place (#555) —, et ce qui s'écrit ici se relit là-bas. */}
        {contact !== null && (
          <ContactsCard
            contact={contact}
            owner={null}
            readOnly={readOnly}
            otherPersonPresent={isOtherPersonPresent(draft)}
            signerCapacity={draft.signerCapacity}
            onSignerCapacityChange={
              onDraftChange === undefined
                ? undefined
                : (signerCapacity) => onDraftChange({ signerCapacity })
            }
            onOtherPersonPresentChange={
              onDraftChange === undefined
                ? undefined
                : (present) => onDraftChange({ otherPersonPresent: present })
            }
            attendee={
              <AttendeeFields draft={draft} readOnly={readOnly} onDraftChange={onDraftChange} />
            }
          />
        )}
        {contact === null && (
          <AttendeeCard draft={draft} readOnly={readOnly} onDraftChange={onDraftChange} />
        )}
        <Card>
          <CardHeader icon={<BadgeCheck size={19} />} title="Mentions du signataire" />
          <CardBody>
            <div className={s.mentions}>
              {CERTIFICATIONS.map((certification) => (
                <label key={certification.key} className={s.mention}>
                  <input
                    type="checkbox"
                    // `=== true` : un brouillon ouvert avant le consentement n'en a pas la clé.
                    checked={draft[certification.key] === true}
                    disabled={readOnly}
                    onChange={(event) =>
                      onDraftChange?.({ [certification.key]: event.target.checked })
                    }
                  />
                  {/* Tout le texte dans **un seul** élément : la case et la phrase sont les deux
                    enfants d'une boîte flex, et un marquage posé en frère de la phrase s'en
                    détacherait de toute la gouttière. */}
                  <span>
                    {certification.label}
                    {certification.required && (
                      <>
                        {' '}
                        {/* L'astérisque ne dit rien que la mention qui suit ne dise : à la
                          synthèse vocale, il n'ajouterait qu'un « astérisque » devant. */}
                        <span className={s.requiredMark} aria-hidden>
                          *
                        </span>{' '}
                        <span className={s.requiredHint}>— obligatoire pour signer</span>
                      </>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader icon={<PenTool size={19} />} title="Signature">
            {sealed && signedOn !== null && (
              <Badge tone="ok">
                <Lock size={14} aria-hidden /> Signée le {signedOn.day} à {signedOn.time}
              </Badge>
            )}
          </CardHeader>
          <CardBody>
            {/* Ambre et sans cadre — le `.sign-gate-note` du prototype : rien n'est en échec,
              une condition est en attente. Un encadré rouge se lirait comme une panne. */}
            {!certified && !readOnly && !sealed && (
              <InfoNote tone="warn" className={s.gateNote} icon={<Lock size={15} />} role="status">
                Cochez la mention obligatoire ci-dessus pour débloquer la signature.
              </InfoNote>
            )}
            <SignaturePad
              disabled={readOnly || !certified || sealed}
              onSigned={onSign}
              onCleared={onSignatureClear}
              ink={ink}
              onSealed={
                readOnly || sealed || draft.signedAt === null
                  ? undefined
                  : () => onDraftChange?.({ signatureLockedAt: new Date().toISOString() })
              }
              sealedHint={sealed ? `Signé le ${signedOn?.day ?? draft.signedAt}` : undefined}
            />
            {/* L'horodatage et le point relevés au paraphe (M20) : produits par la tablette,
              donc en lecture — les deux colonnes du prototype sous la zone de signature. */}
            {draft.signedAt !== null && (
              <div className={s.stamp}>
                <DataField label="Date & heure">
                  <FieldValue mono readOnly>
                    {stampLabel(draft.signedAt) ?? draft.signedAt}
                  </FieldValue>
                </DataField>
                <DataField label="Position">
                  <FieldValue mono readOnly empty={draft.lat === null}>
                    {draft.lat === null
                      ? 'Non relevée'
                      : `${draft.lat.toFixed(5)}, ${(draft.lon ?? 0).toFixed(5)}`}
                  </FieldValue>
                </DataField>
              </div>
            )}
          </CardBody>
        </Card>

        {/*
         * **Après la signature**, qui la date : placée en tête, la carte invitait l'agent à la
         * remplir avant de faire signer (24/09).
         *
         * « Date du contrôle » — les deux dates **côte à côte** (#115). Le rendez-vous vient du
         * bureau et ne se modifie pas ici ; le jour du passage est celui que l'agent déclare.
         * Quand ils divergent, l'écran le dit et n'aligne rien : un contrôle reporté doit
         * rester lisible comme tel jusqu'à la validation (M21).
         */}
        <Card>
          <CardHeader icon={<CalendarClock size={19} />} title="Date du contrôle" />
          <CardBody>
            <div className={s.identity}>
              <DataField label="Rendez-vous pris">
                <FieldValue readOnly empty={scheduledAt === null}>
                  {/* Le même horodatage que la signature ; brut s'il est illisible (M21). */}
                  {scheduledAt === null
                    ? 'Aucun rendez-vous enregistré'
                    : (stampLabel(scheduledAt) ?? scheduledAt)}
                </FieldValue>
              </DataField>
              {/*
               * Même affordance M20 que « En qualité de » plus bas — « — à renseigner » sur
               * l'étiquette et le liseré ambre : deux grammaires visuelles pour un même champ
               * réclamé se liraient comme deux exigences différentes.
               *
               * `max` : le sélecteur natif monte à l'année 275760 à la flèche, et une date que
               * `performedOnSchema` refuse afficherait un jour dans le champ pendant que la
               * carte le dit vide.
               */}
              <Field
                label="Contrôle réalisé le"
                className={isBlank(draft.performedOn) ? claim.claimed : undefined}
              >
                {({ id }) => (
                  <input
                    id={id}
                    className={
                      isBlank(draft.performedOn)
                        ? `${s.dateInput} ${claim.claimedInput}`
                        : s.dateInput
                    }
                    type="date"
                    max="9999-12-31"
                    value={draft.performedOn ?? ''}
                    disabled={readOnly}
                    onChange={(event) =>
                      onDraftChange?.({
                        performedOn: event.target.value === '' ? null : event.target.value,
                      })
                    }
                  />
                )}
              </Field>
            </div>

            {/* `role="status"` : l'écart apparaît en changeant la date, il doit être annoncé
                sans voler le focus au champ que l'agent est en train de corriger. */}
            {deviation.kind === 'shifted' && (
              <InfoNote
                tone="warn"
                className={s.note}
                icon={<CalendarClock size={15} />}
                role="status"
              >
                {describeAppointmentDeviation(deviation)} La date du passage est conservée telle
                quelle : le bureau verra l'écart avec le rendez-vous.
              </InfoNote>
            )}
            {deviation.kind === 'undated' && (
              <InfoNote
                tone="warn"
                className={s.note}
                icon={<CalendarClock size={15} />}
                role="status"
              >
                Le jour du passage n'est pas renseigné : sans lui, le bureau ne saura pas quand le
                contrôle a eu lieu.
              </InfoNote>
            )}
            {deviation.kind === 'no_appointment' && (
              <InfoNote className={s.note} icon={<Info size={15} />}>
                Aucun rendez-vous n'a été enregistré pour ce contrôle : il n'y a rien à comparer, et
                ce n'est pas une anomalie.
              </InfoNote>
            )}
          </CardBody>
        </Card>
      </div>

      <div className={s.column}>
        {/* « Récapitulatif du contrôle » du prototype validé, conclusion en tête comme chez lui
          (`screens.js`, ligne du même nom). C'est le dernier écran avant un geste irréversible :
          ce que l'agent transmet doit s'y lire, fût-il **dérivé** des anomalies (M19). */}
        <Card>
          <CardHeader icon={<ClipboardCheck size={19} />} title="Récapitulatif du contrôle" />
          <CardBody>
            <dl className={s.recap}>
              <div>
                <dt>Conclusion</dt>
                <dd>
                  <Badge tone={conclusion.tone}>{conclusion.label}</Badge>
                </dd>
              </div>
              <div>
                <dt>Anomalies relevées</dt>
                <dd>{recorded === 0 ? 'Aucune' : recorded}</dd>
              </div>
              <div>
                <dt>Personne rencontrée</dt>
                <dd>{describeMetPerson(signed)}</dd>
              </div>
              <div>
                {/* ⚠️ `isAttachment` et non « tout sauf la signature » : la tablette produit
                    aussi l'image d'archive du schéma (M11). La ligne de partage est écrite une
                    seule fois, et c'est ce qui empêche deux compteurs de se contredire sur la
                    même fiche — « 1 ici, 2 là » est exactement le défaut de #181. */}
                <dt>Photos</dt>
                <dd>{media.filter(isAttachment).length}</dd>
              </div>
            </dl>
          </CardBody>
        </Card>

        {/* La carte d'envoi n'a pas d'en-tête : le bouton se nomme lui-même, et le prototype
          pose son « Transmettre le contrôle » nu au bas de la colonne. */}
        <Card>
          <CardBody>
            {locked ? (
              <>
                <div className={s.transmitted}>
                  {returned ? (
                    <>
                      <Undo2 size={18} aria-hidden />
                      <span>Contrôle transmis, puis renvoyé par le bureau pour correction.</span>
                    </>
                  ) : (
                    <>
                      <CheckCheck size={18} aria-hidden />
                      <span>Contrôle transmis — il ne peut plus être modifié.</span>
                    </>
                  )}
                  {syncing !== null && <Badge tone={syncing.tone}>{syncing.label}</Badge>}
                  {/* Le compteur est le retour visible de « Réessayer l'envoi » : il n'a de
                      sens que tant que l'envoi n'est pas passé. Sur un `sent`, « 1 tentative »
                      se lirait comme un incident là où tout s'est bien passé. */}
                  {sync !== 'sent' && (syncAttempts ?? 0) > 0 && (
                    <span className={s.attempts} role="status">
                      {syncAttempts === 1 ? '1 tentative' : `${syncAttempts} tentatives`}
                    </span>
                  )}
                </div>
                {/* Le motif rédigé par la file (« À vérifier avec le bureau… ») doit atteindre
                l'agent : une pastille « En échec » seule ne dit pas quoi faire. */}
                {syncError != null && (
                  <p className={s.syncError} role="alert">
                    <TriangleAlert size={18} aria-hidden />
                    {syncError}
                  </p>
                )}
                {(sync === 'pending' || sync === 'failed') && onRetrySend !== undefined && (
                  <Button tone="ghost" size="sm" onClick={onRetrySend}>
                    Réessayer l'envoi
                  </Button>
                )}
                {/* Refusé ou en échec = rien n'est enregistré côté serveur : rouvrir la fiche
                (`retrySubmission`), la corriger et la retransmettre. Sur un échec, c'est la seule
                sortie quand le même corps rendrait le même refus — un envoi trop lourd (#246). */}
                {(sync === 'rejected' || sync === 'failed') && onReopen !== undefined && (
                  <Button tone="ghost" size="sm" onClick={onReopen}>
                    Rouvrir la fiche pour la corriger
                  </Button>
                )}
                {/* Renvoyé = le dépôt, lui, est bien arrivé : la reprise tire une clé d'envoi
                neuve (`reopenReturned`), sans quoi le serveur rejouerait sa première réponse et
                la correction se perdrait en silence. Le bandeau de tête porte le même geste —
                l'agent arrive par l'un ou par l'autre selon qu'il lit le motif ou qu'il vient
                retransmettre. */}
                {returned && onReopenReturned !== undefined && (
                  <Button tone="ghost" size="sm" onClick={onReopenReturned}>
                    Reprendre le contrôle pour le corriger
                  </Button>
                )}
                {/* Le prototype quitte la fiche de lui-même une fois le contrôle parti ; ici
                    c'est l'agent qui décide, et l'accusé au-dessus reste lisible tant qu'il
                    ne l'a pas fait. */}
                {sync === 'sent' && !returned && history}
              </>
            ) : (
              <>
                {/* Le seul dispositif M21 de l'écran : annoncé (`aria-live`) et rattaché au
                bouton (`aria-describedby`) — il ne doit pas s'évaporer pour l'utilisateur
                qui en dépend le plus. */}
                {warnings.length > 0 && (
                  <ul className={s.warnings} id={warningsId} aria-live="polite">
                    {warnings.map((warning) => (
                      <li key={warning}>
                        <TriangleAlert size={18} aria-hidden />
                        {warning}
                      </li>
                    ))}
                  </ul>
                )}
                {confirming ? (
                  <>
                    <p className={s.gate} role="alert">
                      <Lock size={18} aria-hidden />
                      Une fois transmis, le contrôle ne pourra plus être modifié sur la tablette.
                    </p>
                    <div className={s.confirm}>
                      <Button
                        tone="ghost"
                        size="lg"
                        disabled={submitted}
                        onClick={() => setConfirming(false)}
                      >
                        Annuler
                      </Button>
                      <Button
                        tone="brand"
                        size="lg"
                        fullWidth
                        disabled={submitted || onSubmit === undefined}
                        onClick={() => {
                          setSubmitted(true)
                          onSubmit?.()
                        }}
                      >
                        <Send size={18} aria-hidden /> Confirmer la transmission
                      </Button>
                    </div>
                  </>
                ) : (
                  <Button
                    tone="brand"
                    size="lg"
                    fullWidth
                    disabled={onSubmit === undefined}
                    aria-describedby={warnings.length > 0 ? warningsId : undefined}
                    onClick={() => setConfirming(true)}
                  >
                    <Send size={18} aria-hidden /> Transmettre le contrôle
                  </Button>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

/**
 * Ce que le back-office devra regarder de près — **dérivé, jamais stocké** (M21) : une liste
 * d'avertissements enregistrée serait à resynchroniser à chaque modification du brouillon.
 *
 * Chaque phrase dit *quoi* et *où*, pas « valeur invalide ». Aucune n'empêche l'envoi.
 */
/**
 * Le poids sérialisé à partir duquel l'écran le dit — 700 Kio, contre le mégaoctet que le dépôt
 * accepte.
 *
 * ⚠️ La marge n'est pas de la prudence : le corps transporte aussi les anomalies, les
 * références de pièces et l'enveloppe, et un `413` coûte un aller-retour — la file le classe en
 * échec définitif, et l'agent doit rouvrir la fiche, alléger le schéma et retransmettre (#246).
 */
export const MAX_COMFORTABLE_SURVEY_BYTES = 700 * 1024

/**
 * Le poids sérialisé du relevé, **mémoïsé sur l'objet lui-même**.
 *
 * ⚠️ La mémoïsation n'est pas de l'optimisation prématurée : cette liste est recalculée à chaque
 * rendu de l'onglet, donc à chaque frappe dans « Personne rencontrée », et sérialiser un relevé
 * d'un mégaoctet coûte une quinzaine de millisecondes sur un portable — quatre à six fois plus
 * sur une tablette. Un patch qui ne touche pas au relevé garde la même référence
 * (`{ ...current, ...patch }`), donc la mesure ne se refait qu'au geste de dessin suivant — qui
 * réécrit de toute façon le relevé entier dans Dexie.
 *
 * On mesure le **relevé**, pas le brouillon entier : c'est la seule partie qui grossisse sans
 * que l'agent s'en aperçoive, et c'est celle qui reste stable sous la frappe.
 */
const SURVEY_BYTES = new WeakMap<object, number>()

function surveyBytes(survey: InspectionDraft['survey']): number {
  const known = SURVEY_BYTES.get(survey)

  if (known !== undefined) return known

  const bytes = new TextEncoder().encode(JSON.stringify(survey)).length
  SURVEY_BYTES.set(survey, bytes)

  return bytes
}

export function submissionWarnings(
  draft: InspectionDraft,
  media: LocalMedium[],
  // ⚠️ L'écart **déjà calculé**, pas le rendez-vous : recalculé ici, il pouvait diverger de
  // celui de la carte au premier changement d'entrée — la carte annonçant un report pendant
  // que la dernière liste lue avant la transmission n'en disait rien.
  deviation: AppointmentDeviation,
  vocabulary: readonly VocabularyEntry[] = [],
): string[] {
  const warnings: string[] = []

  // ⚠️ Répété ici alors que la carte le dit déjà, et c'est délibéré : cette liste est la
  // dernière chose lue avant un geste irréversible, et l'agent transmet depuis le bas de la
  // colonne de droite — sans cette ligne, il part sans avoir revu le haut de la gauche.
  // La phrase du domaine, la même que celle de la carte : `null` dès qu'il n'y a pas d'écart.
  const shift = describeAppointmentDeviation(deviation)

  if (shift !== null) {
    warnings.push(shift)
  }

  if (deviation.kind === 'undated') {
    warnings.push('Le jour où le contrôle a été réalisé n’est pas renseigné.')
  }

  if (isBlank(draft.metPerson?.capacity)) {
    warnings.push("La qualité du signataire (« en qualité de ») n'est pas renseignée, ci-dessus.")
  }

  const schematic = draft.survey.schematic

  // ⚠️ **Le dessin ne compte pas comme relevé.** Depuis M11, dessiner écrit `survey.schematic` :
  // compter les clés du relevé entier faisait disparaître cet avertissement au premier trait,
  // sur une fiche dont aucune case n'était remplie — l'écran affirmait alors le contraire de la
  // vérité, sur la dernière page lue avant un acte irréversible.
  if (Object.keys(draft.survey).filter((field) => field !== 'schematic').length === 0) {
    warnings.push('Aucune réponse saisie dans l’onglet « Relevé ».')
  }

  // ⚠️ M11 : « l'image garantit que l'archive se relit dans dix ans », et c'est la tablette qui
  // la produit — « l'agent exporte avant de transmettre ». Rien à l'écran ne montre qu'elle
  // manque, le média `schematic` n'étant ni une pièce jointe ni un élément du plan, et le
  // contrôle devient immuable une fois parti.
  if (
    schematic !== undefined &&
    schematic.nodes.length > 0 &&
    !media.some((medium) => medium.type === 'schematic')
  ) {
    warnings.push('Le schéma dessiné n’a pas été exporté en image.')
  }

  // ⚠️ **Une image jointe ne dit pas qu'elle montre le dessin en cours.** Exporter puis poser
  // la forme dont on se souvient laisse partir une archive amputée, et le bureau ne voit que
  // cette image-là. La règle vit dans `hasOutdatedSchematicImage`, que l'éditeur lit aussi :
  // deux calculs finiraient par dire deux choses sur le même contrôle.
  if (hasOutdatedSchematicImage(schematic, media)) {
    warnings.push('L’image du schéma jointe est antérieure à votre dernière retouche du dessin.')
  }

  // ⚠️ L'éditeur cesse d'allonger un tracé arrivé au plafond, pour garder le dépôt sous sa
  // limite d'un mégaoctet — mais rien dans le dessin ne montre que la fin du geste manque, et
  // l'agent est le seul à pouvoir la reprendre, devant la maison (M21).
  if (schematic !== undefined && hasTruncatedSchematicStroke(schematic)) {
    warnings.push(
      'Un tracé du schéma a atteint sa longueur maximale : la fin du geste n’a pas été enregistrée.',
    )
  }

  // ⚠️ **Le poids réel, mesuré — pas déduit du nombre de points.** Les deux plafonds ci-dessus
  // bornent des gestes ; ce qui fait échouer un envoi est un nombre d'octets, et trente tracés
  // ordinaires (191 Kio) et trente tracés au plafond (1297 Kio) passent tous les deux à travers
  // eux. Au-delà de la limite du dépôt, le `413` tombe avant le contrôleur et la file le classe
  // en échec définitif : mieux vaut que l'agent allège avant de transmettre qu'après (#246).
  const bytes = surveyBytes(draft.survey)

  if (bytes > MAX_COMFORTABLE_SURVEY_BYTES) {
    warnings.push(
      `Le relevé pèse ${Math.round(bytes / 1024)} Kio, le schéma dessiné pour l’essentiel : au-delà d’un mégaoctet, le serveur refusera l’envoi.`,
    )
  }

  // ⚠️ L'autre axe du même plafond : l'éditeur cesse de poser des formes, et le modèle, lui,
  // ne refuse plus rien — un `.max` sur `nodes` faisait tomber le relevé entier au dépôt, donc
  // un contrôle verrouillé et intransmissible, sans qu'aucun écran ne nomme le dessin.
  if (schematic !== undefined && isSchematicPlanFull(schematic)) {
    warnings.push(
      `Le plan du schéma porte ${MAX_SCHEMATIC_NODES} formes, le maximum : l’éditeur n’en pose plus.`,
    )
  }

  // ⚠️ **Une conclusion posée à la main survit à ce que le relevé établit** (M19, et c'est
  // `withAnomalyConclusion` qui la laisse vivre) : elle ne peut donc pas être muette ici. Le
  // prototype ne dit rien de ce cas — il n'a ni surcharge ni retour à la déduction —, et M21
  // tranche : on enregistre, on signale, le bureau arbitre.
  //
  // La phrase nomme les deux faits que l'agent a sous les yeux — sa conclusion et ce qu'il a
  // relevé —, jamais la règle : « déduction » est un mot du modèle, pas le sien.
  const conclusion = resolveInspectionResult(draft)

  if (conclusion.overridden && conclusion.value !== conclusion.derived) {
    const posed = RESULT_PRESENTATION[conclusion.value].label

    warnings.push(
      recordedAnomalyCount(draft) === 0
        ? `La conclusion « ${posed} » a été posée à la main : aucune anomalie n’est pourtant relevée.`
        : `La conclusion « ${posed} » a été posée à la main : elle ne s’accorde pas avec les anomalies relevées.`,
    )
  }

  if (recordedAnomalyCount(draft) === 0) {
    warnings.push('Aucune anomalie relevée sur ce contrôle.')
  }

  // #312 — la règle que la carte « Anomalies relevées » affiche déjà sous chaque anomalie.
  for (const anomaly of draft.anomalies) {
    const designation = fixtureDesignationWarning(anomaly, draft.survey, vocabulary)
    if (designation !== null) warnings.push(designation)
  }

  // « La grille qui signale » : un point d'eau compté « Non » attend son anomalie.
  for (const cell of fixturesToFlag(draft.survey, draft.anomalies)) {
    warnings.push(fixtureFlagWarning(cell, vocabulary))
  }

  warnings.push(...stormwaterWarnings(draft.survey, vocabulary))

  // ⚠️ `isAttachment`, sinon l'image d'archive du schéma passe pour une photo : un agent qui a
  // exporté son dessin et n'a pris aucun cliché ne recevait plus l'avertissement.
  if (!media.some(isAttachment)) {
    warnings.push('Aucune photo jointe au contrôle.')
  }

  // #358 — la vignette le dit déjà ; l'agent qui ne repasse pas par l'onglet l'apprend ici.
  const unnamed = media.filter(
    (medium) => isAttachment(medium) && (medium.label ?? '').trim() === '',
  ).length

  if (unnamed > 0) {
    warnings.push(
      unnamed === 1
        ? 'Une pièce jointe n’a pas de nom.'
        : `${String(unnamed)} pièces jointes n’ont pas de nom.`,
    )
  }

  if (draft.signedAt === null) {
    warnings.push("Le contrôle n'est pas signé.")
  } else if (draft.lat === null) {
    // ⚠️ **Le fait, jamais la cause** (#141) : la position manque aussi quand l'agent a coupé le
    // géoréférencement dans « Réglages », et « n'a pas pu être relevée » lui annoncerait alors
    // une panne de GPS qui n'a pas eu lieu.
    warnings.push('Aucune position n’a été relevée au moment de la signature.')
  }

  return warnings
}

/**
 * La signature ne s'efface plus une fois validée ou transmise (#352). `lockedAt` couvre la fiche
 * transmise avant que `signatureLockedAt` n'existe : une signature partie n'est pas moins acquise.
 */
function isSignatureSealed(draft: InspectionDraft): boolean {
  return draft.signedAt !== null && (draft.signatureLockedAt != null || draft.lockedAt !== null)
}

/** Le signataire tel que le récapitulatif l'annonce — « — » plutôt qu'une ligne vide. */
function describeMetPerson(draft: InspectionDraft): string {
  const name = [draft.metPerson?.firstName, draft.metPerson?.lastName].filter(Boolean).join(' ')
  const capacity = draft.metPerson?.capacity

  if (name === '') return '—'

  return isBlank(capacity) ? name : `${name} · ${String(capacity)}`
}

function isBlank(value: string | null | undefined): boolean {
  return value === null || value === undefined || value.trim() === ''
}
